import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setNearbyDogsCache } from "../../redux/userSlice";
import {
  List,
  Heart,
  Bone,
  ChevronLeft,
  ChevronRight,
  Dot,
  Loader2,
} from "lucide-react";
import "./ListDashboardPage.css";
import mapIcon from "../../assets/dashboardMapIcon.svg";
import pawPrintIcon from "../../assets/pawIcon.svg";
import apiClient from "../../api/apiClient";
import { ENDPOINTS } from "../../api/endPoints";
import FilterPanel from "../../components/FilterPanel";
import TopNavBar from "../../components/TopNavBar";
import PermissionEnforcer from "../../components/PermissionEnforcer";
import dogPlaceholder from "../../assets/dogPlaceholder1.png";
import userPlaceholder from "../../assets/userPlaceholder1.png";
import appIcon from "../../assets/appIcon.svg";
import filterW from "../../assets/topNavBarIcons/filterW.svg";

const loadedImageCache = new Set();

const CardImage = ({ src, placeholder, alt, className, style }) => {
  const [isLoading, setIsLoading] = useState(() => {
    return src && src !== placeholder && !loadedImageCache.has(src);
  });
  const [imgSrc, setImgSrc] = useState(() => {
    if (!src || src === placeholder) return placeholder;
    return loadedImageCache.has(src) ? src : placeholder;
  });

  useEffect(() => {
    if (!src || src === placeholder) {
      setImgSrc(placeholder);
      setIsLoading(false);
      return;
    }

    if (loadedImageCache.has(src)) {
      setImgSrc(src);
      setIsLoading(false);
      return;
    }

    setImgSrc(placeholder);
    setIsLoading(true);
    let isMounted = true;

    const img = new window.Image();
    img.src = src;

    img.onload = () => {
      if (isMounted) {
        loadedImageCache.add(src);
        setImgSrc(src);
        setIsLoading(false);
      }
    };

    img.onerror = () => {
      if (isMounted) {
        setImgSrc(placeholder);
        setIsLoading(false);
      }
    };

    return () => {
      isMounted = false;
    };
  }, [src, placeholder]);

  return (
    <>
      <img
        src={imgSrc}
        alt={alt}
        className={className}
        style={{
          ...style,
          opacity: isLoading ? 0.5 : 1,
          transition: "opacity 0.3s ease-in-out",
        }}
      />
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          <Loader2 className="animate-spin" size={32} color="#005b7d" />
        </div>
      )}
    </>
  );
};

const formatName = (name) => {
  if (!name) return "";

  const words = name.split(/[\s\-]+/);

  const firstTwo = words.slice(0, 2).join(" ");

  if (firstTwo.length > 16) {
    return firstTwo.substring(0, 16) + "..";
  }

  return firstTwo;
};

const ListDashboardPage = ({
  onSwitchToMap,
  filterData,
  setFilterData,
  filteredDogs,
  setFilteredDogs,
  isFilterActive,
  setIsFilterActive,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    profile,
    nearbyDogs: cachedDogs,
    loading: profileLoading,
  } = useSelector((state) => state.user);

  const scrollRef = useRef(null);

  const getSavedUserLocation = () => {
    const saved = localStorage.getItem("user_location_coords");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [nearbyDogs, setNearbyDogs] = useState(cachedDogs || []);
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(getSavedUserLocation());
  const [flippedCards, setFlippedCards] = useState({});
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const lastFetchedLocRef = useRef(getSavedUserLocation());
  const hasAutoShownTooltip = useRef(false);
  const hasFetchedInitialRef = useRef(false);

  const [showFlipTooltipId, setShowFlipTooltipId] = useState(null);
  const [peekingCardId, setPeekingCardId] = useState(null);
  const [hasSeenFlipTooltip, setHasSeenFlipTooltip] = useState(() => {
    return localStorage.getItem("hasSeenFlipTooltip") === "true";
  });

  useEffect(() => {
    const savedIsFilterActive = sessionStorage.getItem(
      "social_paws_isFilterActive",
    );
    if (savedIsFilterActive === "true" && !isFilterActive) {
      try {
        const savedFilteredDogs = JSON.parse(
          sessionStorage.getItem("social_paws_filteredDogs"),
        );
        const savedFilterData = JSON.parse(
          sessionStorage.getItem("social_paws_filterData"),
        );

        if (savedFilteredDogs) setFilteredDogs(savedFilteredDogs);
        if (savedFilterData) setFilterData(savedFilterData);
        setIsFilterActive(true);
      } catch (e) {
        console.error("Failed to parse saved filters", e);
      }
    }
  }, []);

  const profileId = profile?.id;
  const isLoggedPupParent =
    profile?.team_paws === 1 || profile?.user_dog_status === 1;

  const isLoggedPupPal =
    (profile?.team_social === 1 && profile?.team_paws !== 1) ||
    profile?.user_dog_status === 0;

  const fetchNearbyDogs = useCallback(
    async (lat, lng, force = false) => {
      if (!profileId) return;

      if (cachedDogs && cachedDogs.length > 0) {
        setNearbyDogs(cachedDogs);
        if (!force) return;
      }

      setIsApiLoading(true);
      try {
        const payload = {
          user_id: profileId,
          latitude: lat,
          longitude: lng,
        };

        const response = await apiClient.post(
          ENDPOINTS.GET_NEARBY_DOGS,
          payload,
        );

        if (response.data?.status) {
          let fetchedDogs = response.data.dogs || [];

          fetchedDogs = fetchedDogs.filter(
            (dog) =>
              dog.is_old_user !== 1 &&
              dog.is_old_user !== true &&
              String(dog.user_id) !== String(profileId),
          );

          if (!isLoggedPupParent && isLoggedPupPal) {
            fetchedDogs = fetchedDogs.filter(
              (dog) => dog.team_paws === 1 || dog.user_dog_status === 1,
            );
          }

          setNearbyDogs(fetchedDogs);
          dispatch(setNearbyDogsCache(fetchedDogs));
        }
      } catch (error) {
        console.error("Error fetching nearby dogs:", error);
      } finally {
        setIsApiLoading(false);
      }
    },
    [profileId, isLoggedPupParent, isLoggedPupPal, dispatch, cachedDogs],
  );

  const fetchRef = useRef(fetchNearbyDogs);
  useEffect(() => {
    fetchRef.current = fetchNearbyDogs;
  }, [fetchNearbyDogs]);

  // GEOLOCATION LISTENER
  useEffect(() => {
    const savedLoc = getSavedUserLocation();

    if (savedLoc && profileId && !hasFetchedInitialRef.current) {
      hasFetchedInitialRef.current = true;
      lastFetchedLocRef.current = savedLoc;
      fetchRef.current(savedLoc.lat, savedLoc.lng, true);
    }

    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        window.dispatchEvent(new Event("locationGranted"));
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        lastFetchedLocRef.current = pos;
        setUserLocation(pos);
        localStorage.setItem("user_location_coords", JSON.stringify(pos));

        if (!hasFetchedInitialRef.current) {
          hasFetchedInitialRef.current = true;
          fetchRef.current(pos.lat, pos.lng, true);
        }
      },
      (error) => {
        console.error("Current location error:", error);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 10000 },
    );

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        window.dispatchEvent(new Event("locationGranted"));
        const newLat = position.coords.latitude;
        const newLng = position.coords.longitude;
        const lastLoc = lastFetchedLocRef.current;

        if (
          lastLoc &&
          Math.abs(lastLoc.lat - newLat) < 0.01 &&
          Math.abs(lastLoc.lng - newLng) < 0.01
        ) {
          return;
        }

        const pos = { lat: newLat, lng: newLng };
        lastFetchedLocRef.current = pos;

        setUserLocation(pos);
        localStorage.setItem("user_location_coords", JSON.stringify(pos));
        fetchRef.current(pos.lat, pos.lng, true);
      },
      (error) => console.error("Watch location error:", error),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 10000 },
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [profileId]);

  const scroll = (direction) => {
    if (scrollRef.current && window.innerWidth >= 1024) {
      const { current } = scrollRef;
      const scrollAmount = 260;
      if (direction === "left") {
        current.scrollBy({ left: -scrollAmount, behavior: "smooth" });
      } else {
        current.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
      setTimeout(handleScrollIndicators, 100);
    }
  };

  const displayDogs = useMemo(() => {
    let dogsToDisplay = isFilterActive ? filteredDogs : nearbyDogs;

    dogsToDisplay = dogsToDisplay.filter((dog) => {
      if (dog.is_old_user === 1 || dog.is_old_user === true) {
        if (
          dog.isDogProfileComplete !== 1 &&
          dog.isDogProfileComplete !== true &&
          dog.isDogProfileHalfComplete !== 1 &&
          dog.isDogProfileHalfComplete !== true
        ) {
          return false;
        }
      }

      const lat = dog.user_latitude;
      const lng = dog.user_longitude;

      if (lat == null || lat === "" || lng == null || lng === "") return false;

      const numLat = parseFloat(lat);
      const numLng = parseFloat(lng);

      if (isNaN(numLat) || numLat === 0 || isNaN(numLng) || numLng === 0) {
        return false;
      }

      return true;
    });

    if (!isLoggedPupParent && isLoggedPupPal) {
      dogsToDisplay = dogsToDisplay.filter(
        (dog) => dog.team_paws === 1 || dog.user_dog_status === 1,
      );
    }

    return dogsToDisplay;
  }, [
    isFilterActive,
    filteredDogs,
    nearbyDogs,
    isLoggedPupParent,
    isLoggedPupPal,
    profileId,
  ]);

  useEffect(() => {
    if (
      !hasSeenFlipTooltip &&
      !hasAutoShownTooltip.current &&
      displayDogs.length > 0
    ) {
      const isMobile = window.innerWidth < 1024;

      const eligibleTargetDog = displayDogs.find(
        (d) => d.team_paws === 1 || d.user_dog_status === 1,
      );

      let finalTarget = eligibleTargetDog || displayDogs[0];

      if (finalTarget) {
        if (isMobile) {
          hasAutoShownTooltip.current = true;

          setTimeout(() => {
            const cardElement = document.getElementById(
              `profile-card-wrapper-${finalTarget.id}`,
            );

            if (cardElement) {
              cardElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
              setTimeout(() => {
                setShowFlipTooltipId(finalTarget.id);
              }, 600);
            } else {
              setShowFlipTooltipId(finalTarget.id);
            }
          }, 300);
        } else {
          const initialVisible = displayDogs.slice(0, 3);
          let desktopTarget =
            initialVisible.length > 1 ? initialVisible[1] : initialVisible[0];

          if (
            desktopTarget?.team_paws !== 1 &&
            desktopTarget?.user_dog_status !== 1
          ) {
            desktopTarget = eligibleTargetDog || desktopTarget;
          }

          setShowFlipTooltipId(desktopTarget.id);
          hasAutoShownTooltip.current = true;
        }
      }
    }
  }, [displayDogs, hasSeenFlipTooltip]);

  const handleScrollIndicators = useCallback(() => {
    if (scrollRef.current && window.innerWidth >= 1024) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 10);
    }
  }, []);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer && window.innerWidth >= 1024) {
      handleScrollIndicators();
      scrollContainer.addEventListener("scroll", handleScrollIndicators);
      window.addEventListener("resize", handleScrollIndicators);
      return () => {
        scrollContainer.removeEventListener("scroll", handleScrollIndicators);
        window.removeEventListener("resize", handleScrollIndicators);
      };
    }
  }, [displayDogs, handleScrollIndicators]);

  const handleFlip = (e, dogId) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    setFlippedCards((prev) => ({
      ...prev,
      [dogId]: !prev[dogId],
    }));
  };

  const handleApplyFilters = (newFilteredList) => {
    setIsFilterActive(true);
    setFilteredDogs(newFilteredList);

    sessionStorage.setItem("social_paws_isFilterActive", "true");
    sessionStorage.setItem(
      "social_paws_filteredDogs",
      JSON.stringify(newFilteredList),
    );
    sessionStorage.setItem(
      "social_paws_filterData",
      JSON.stringify(filterData),
    );
  };

  const handleClearFilters = () => {
    setIsFilterActive(false);
    setFilteredDogs([]);
    setIsFilterOpen(false);
    setFilterData({
      teamSelection: "both",
      proximityValue: 5,
      availability: [""],
      breed: "",
      experience: [""],
      age: "",
    });

    sessionStorage.removeItem("social_paws_isFilterActive");
    sessionStorage.removeItem("social_paws_filteredDogs");
    sessionStorage.removeItem("social_paws_filterData");
  };

  const handleOnPress = (dog, isFlipped) => {
    sessionStorage.setItem("social_paws_last_profile_id", dog.id);
    navigate("/profile-details", {
      state: { dogData: dog, activeView: isFlipped ? "user" : "dog" },
    });
  };

  useEffect(() => {
    const lastProfileId = sessionStorage.getItem("social_paws_last_profile_id");

    if (lastProfileId && displayDogs.length > 0) {
      const scrollTimeout = setTimeout(() => {
        const cardElement = document.getElementById(
          `profile-card-${lastProfileId}`,
        );

        if (cardElement) {
          cardElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "center",
          });

          sessionStorage.removeItem("social_paws_last_profile_id");
        }
      }, 150);

      return () => clearTimeout(scrollTimeout);
    }
  }, [displayDogs]);

  const formatDistance = (miles = 0) => {
    const blocks = Math.round(miles * 20);

    if (blocks <= 20) {
      return `${blocks} ${blocks === 1 ? "block" : "blocks"} away`;
    }

    const roundedMiles = Number(miles.toFixed(1));
    return `${roundedMiles} ${roundedMiles === 1 ? "mile" : "miles"} away`;
  };

  return (
    <div className="list-container-l">
      <PermissionEnforcer />

      {showFlipTooltipId && !hasSeenFlipTooltip && (
        <div
          className="tooltip-global-backdrop"
          onClick={(e) => {
            e.stopPropagation();
            handleFlip(e, showFlipTooltipId);
            localStorage.setItem("hasSeenFlipTooltip", "true");
            setHasSeenFlipTooltip(true);
            setShowFlipTooltipId(null);
          }}
        />
      )}

      {/* DESKTOP Header */}
      <div
        className="list-desktop-only"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          boxSizing: "border-box",
          position: "relative",
          zIndex: 10,
          padding: "0 1.5rem",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "10px",
            marginTop: "16px",
          }}
        >
          <img
            src={appIcon}
            alt="Social Paws"
            style={{ width: "28px", height: "28px", objectFit: "contain" }}
          />
          <span
            style={{
              color: "#fff",
              fontSize: "22px",
              fontWeight: "700",
            }}
          >
            Social Paws
          </span>
          <span
            style={{
              color: "#fff",
              fontSize: "18px",
              marginLeft: "1rem",
              fontWeight: "700",
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => navigate("/help")}
          >
            Need Help?
          </span>
        </div>

        <TopNavBar theme="dark" />
      </div>

      {/* MOBILE Header */}
      <div className="list-mobile-header list-mobile-only">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <span
            style={{
              color: "#fff",
              fontSize: "1rem",
              cursor: "pointer",
              textDecoration: "underline",
              fontWeight: "700",
              marginBottom: "12px",
            }}
            onClick={() => navigate("/help")}
          >
            Need Help?
          </span>
          <div className="list-mobile-logo-group">
            <img src={appIcon} alt="Social Paws" className="list-mobile-logo" />
            <span className="list-mobile-title">Social Paws</span>
          </div>
        </div>
        <TopNavBar theme="dark" />
      </div>

      <div className="carousel-wrapper-l">
        {displayDogs.length > 3 && showLeftArrow && (
          <button
            className="nav-arrow-btn-l left-arrow-l list-desktop-only"
            onClick={() => scroll("left")}
          >
            <ChevronLeft size={32} color="#000" />
          </button>
        )}

        <div
          className="cards-scroll-container-l"
          ref={scrollRef}
          style={{
            display: "flex",
            justifyContent: displayDogs.length <= 3 ? "center" : "flex-start",
            width: "100%",
            boxSizing: "border-box",
            gap: "20px",
            padding: "20px",
          }}
        >
          {displayDogs.map((dog, index) => {
            const isFlipped = !!flippedCards[dog.id];
            const isPeeking = peekingCardId === dog.id && !isFlipped;
            const showBackSide = isFlipped;
            const rotationAngle = isFlipped ? 180 : isPeeking ? 45 : 0;
            const isTooltipTarget =
              showFlipTooltipId === dog.id && !hasSeenFlipTooltip;

            const getValidImage = (imgData) => {
              if (Array.isArray(imgData) && imgData.length > 0 && imgData[0]) {
                return imgData[0];
              }
              if (typeof imgData === "string" && imgData.trim() !== "") {
                return imgData;
              }
              return null;
            };

            const fetchedDogImage =
              getValidImage(dog.dog_images) || getValidImage(dog.profile_image);
            const fetchedUserImage =
              getValidImage(dog.user_profile_image) ||
              getValidImage(dog.profile_image);

            const isPupPalOnly =
              (dog.team_social === 1 && dog.team_paws !== 1) ||
              dog.user_dog_status === 0;

            const frontImage = isPupPalOnly
              ? fetchedUserImage || userPlaceholder
              : fetchedDogImage || dogPlaceholder;

            const backImage = fetchedUserImage || userPlaceholder;
            const isUserSide = showBackSide || isPupPalOnly;
            const currentPlaceholder = isUserSide
              ? userPlaceholder
              : dogPlaceholder;

            const dogName = formatName(dog.dog_name);
            const userName = formatName(dog.user_name);

            return (
              <div
                key={dog.id}
                id={`profile-card-wrapper-${dog.id}`}
                className="profile-card-wrapper"
                style={{
                  position: "relative",
                  zIndex: isTooltipTarget ? 160 : 1,
                  transform: `translateY(${
                    window.innerWidth >= 1024 && index % 2 === 0
                      ? "-50px"
                      : window.innerWidth >= 1024
                        ? "50px"
                        : "0px"
                  })`,
                  WebkitTransform: `translateY(${
                    window.innerWidth >= 1024 && index % 2 === 0
                      ? "-50px"
                      : window.innerWidth >= 1024
                        ? "50px"
                        : "0px"
                  })`,
                  transition:
                    "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1), -webkit-transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                <div
                  id={`profile-card-${dog.id}`}
                  onClick={(e) => {
                    if (isTooltipTarget) {
                      e.stopPropagation();
                      if (!hasSeenFlipTooltip) {
                        localStorage.setItem("hasSeenFlipTooltip", "true");
                        setHasSeenFlipTooltip(true);
                        setShowFlipTooltipId(null);
                      }
                      handleFlip(e, dog.id);
                    } else {
                      handleOnPress(dog, showBackSide);
                    }
                  }}
                  className={`profile-card-l ${showBackSide ? "is-flipped" : ""}`}
                  style={{
                    transform: `rotateY(${rotationAngle}deg) translateZ(0)`,
                    WebkitTransform: `rotateY(${rotationAngle}deg) translateZ(0)`,
                    transition:
                      "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1), -webkit-transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ width: "100%", height: "100%" }}>
                    <CardImage
                      src={showBackSide ? backImage : frontImage}
                      placeholder={currentPlaceholder}
                      alt="profile"
                      className="profile-image-l"
                      style={{
                        borderRadius: "16px",
                        objectFit: "cover",
                        transform: showBackSide ? "rotateY(180deg)" : "none",
                        backfaceVisibility: "visible",
                        WebkitBackfaceVisibility: "visible",
                      }}
                    />

                    {(dog.team_paws === 1 || dog.user_dog_status === 1) && (
                      <div
                        className="card-top-flip-area"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!hasSeenFlipTooltip) {
                            localStorage.setItem("hasSeenFlipTooltip", "true");
                            setHasSeenFlipTooltip(true);
                            setShowFlipTooltipId(null);
                          }
                          handleFlip(e, dog.id);
                        }}
                        style={{
                          zIndex: 10,
                          cursor: "pointer",
                          transform: showBackSide ? "rotateY(180deg)" : "none",
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          paddingTop: "15px",
                        }}
                      >
                        <div className="tap-to-flip-badge">Tap to Flip</div>
                      </div>
                    )}

                    <div
                      className="card-overlay-l"
                      style={{
                        transform: showBackSide ? "rotateY(180deg)" : "none",
                      }}
                    >
                      <div className="card-info-l">
                        <div className="card-main-row-l">
                          {dog.team_paws === 1 ? (
                            <Bone className="profile-type-icon-l" />
                          ) : (
                            <Heart className="profile-type-icon-l" />
                          )}
                          <h3 className="profile-name-l">
                            {showBackSide
                              ? userName
                              : isPupPalOnly
                                ? userName
                                : dogName}
                          </h3>
                        </div>

                        <div className="card-sub-row-l">
                          <div
                            className="card-distance-badge"
                            style={{ color: "white" }}
                          >
                            <span className="paw-icon">
                              <img
                                src={pawPrintIcon}
                                alt="paw"
                                className="distance-paw-icon"
                              />
                            </span>
                            {formatDistance(dog.distance_miles)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {isTooltipTarget && (
                  <div className="tooltip-overlay-container">
                    <div className="tooltip-dashed-border"></div>

                    <div className="tooltip-hand-container">
                      <span className="try-it-text">Try it!</span>
                      <svg
                        width="60"
                        height="60"
                        viewBox="0 0 24 24"
                        fill="white"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          filter: "drop-shadow(2px 4px 6px rgba(0,0,0,0.4))",
                        }}
                      >
                        <path
                          stroke="black"
                          strokeWidth="0.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 11.24V7.5C9 6.12 10.12 5 11.5 5C12.88 5 14 6.12 14 7.5v3.74c1.21-.81 2-2.18 2-3.74 0-2.49-2.01-4.5-4.5-4.5S7 5.01 7 7.5c0 1.56.79 2.93 2 3.74zm9.84 4.63l-4.54-2.26c-.17-.07-.35-.11-.54-.11H13v-6c0-.83-.67-1.5-1.5-1.5S10 6.67 10 7.5v10.74l-3.44-1.61c-.34-.16-.76-.09-1.02.2l-1.32 1.5 5.21 5.43c.27.28.64.44 1.03.44h6.06c.7 0 1.32-.47 1.48-1.14l1.45-6.04c.16-.65-.18-1.33-.8-1.63z"
                        />
                      </svg>
                    </div>

                    <div className="tooltip-bottom-section">
                      <div className="tooltip-arrow-up">
                        <svg
                          width="32"
                          height="32"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="white"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="12" y1="19" x2="12" y2="5"></line>
                          <polyline points="5 12 12 5 19 12"></polyline>
                        </svg>
                      </div>
                      <div className="tooltip-bottom-box">
                        Tap the profile card to flip and see complete profile
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {displayDogs.length === 0 && !isApiLoading && (
            <div className="text-white text-center w-full p-10">
              <p style={{ color: "#fff" }}>No dogs found in your area.</p>
            </div>
          )}
        </div>

        {displayDogs.length > 3 && showRightArrow && (
          <button
            className="nav-arrow-btn-l right-arrow-l list-desktop-only"
            onClick={() => scroll("right")}
          >
            <ChevronRight size={32} color="#000" />
          </button>
        )}
      </div>

      <div className="list-footer">
        <div className="nav-item-list" onClick={onSwitchToMap}>
          <Dot className="nav-dot-icon-hidden" />
          <img src={mapIcon} alt="miniMapIcon" className="nav-map-icon" />
          <span className="nav-item-text">MAP VIEW</span>
        </div>
        <div className="nav-divider-list" />
        <div className="nav-item-list active">
          <div className="list-icon-lines-list">
            <Dot
              className="nav-dot-icon-active"
              style={{ marginBottom: "8px" }}
            />
            <List className="nav-list-icon" style={{ marginBottom: "8px" }} />
          </div>
          <span className="nav-item-text">LIST VIEW</span>
        </div>
      </div>

      <button
        className="filter-btn-l"
        onClick={() => setIsFilterOpen(true)}
        title="Filter List"
      >
        <img src={filterW} alt="filterIcon" className="filter-btn-icon-l" />
      </button>

      <FilterPanel
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        filterData={filterData}
        setFilterData={setFilterData}
        onApplyFilters={handleApplyFilters}
        onClear={handleClearFilters}
        profile={profile}
        userLocation={userLocation}
      />
    </div>
  );
};

export default ListDashboardPage;
